/**
 * 页面容器组件
 *
 * @experimental 此组件尚未在消费项目中广泛使用，API 可能调整。
 *
 * 适用于列表页/仪表盘等页面容器。
 * 如果是详情/表单页（create/edit/view），请使用 {@link DetailPageLayout}。
 *
 * 统一的页面包裹组件，提供:
 * - 页面标题 + 副标题
 * - 操作按钮区域
 * - Tab 切换
 * - 内容区域 (Card 或原始)
 */

import { Card, Tabs, theme } from 'antd';
import type React from 'react';
import { useCallback, useState } from 'react';

// ===== 类型定义 =====

/**
 * Tab 配置
 */
export interface PageTab {
  /** Tab key */
  key: string;
  /** Tab 标签 */
  tab: React.ReactNode;
  /** 是否禁用 */
  disabled?: boolean;
}

/**
 * PageContainer 属性
 */
export interface PageContainerProps {
  /** 页面标题 */
  title?: React.ReactNode;
  /** 副标题 */
  subtitle?: React.ReactNode;
  /** 右上角操作区域 */
  actions?: React.ReactNode;
  /** Tab 配置 */
  tabs?: PageTab[];
  /** 当前激活的 Tab key */
  activeTab?: string;
  /** Tab 切换回调 */
  onTabChange?: (key: string) => void;
  /** 是否用 Card 包裹内容, 默认 true */
  wrapCard?: boolean;
  /** 页面间距, 默认 16 */
  gap?: number;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 头部额外内容 (显示在标题和操作按钮之间) */
  headerExtra?: React.ReactNode;
  /** 子内容 */
  children: React.ReactNode;
}

// ===== 组件实现 =====

/**
 * 页面容器组件
 *
 * @example
 * ```tsx
 * // 基础列表页
 * <PageContainer
 *   title="用户管理"
 *   subtitle="管理系统中的所有用户"
 *   actions={
 *     <Button type="primary" onClick={goCreate}>新增用户</Button>
 *   }
 * >
 *   <ProTable ... />
 * </PageContainer>
 *
 * // 带 Tab 的页面
 * <PageContainer
 *   title="系统设置"
 *   tabs={[
 *     { key: 'basic', tab: '基础设置' },
 *     { key: 'security', tab: '安全设置' },
 *     { key: 'notification', tab: '通知设置' },
 *   ]}
 *   activeTab={currentTab}
 *   onTabChange={setCurrentTab}
 * >
 *   {currentTab === 'basic' && <BasicSettings />}
 *   {currentTab === 'security' && <SecuritySettings />}
 *   {currentTab === 'notification' && <NotificationSettings />}
 * </PageContainer>
 *
 * // 无 Card 包裹 (自行管理布局)
 * <PageContainer title="仪表盘" wrapCard={false}>
 *   <StatCardGrid items={statItems} />
 *   <ChartPanel />
 * </PageContainer>
 * ```
 */
export function PageContainer(props: PageContainerProps) {
  const { token } = theme.useToken();
  const {
    title,
    subtitle,
    actions,
    tabs,
    activeTab: controlledActiveTab,
    onTabChange,
    wrapCard = true,
    gap = 16,
    className,
    style,
    headerExtra,
    children,
  } = props;

  // 非受控 Tab 状态
  const [internalTab, setInternalTab] = useState(tabs?.[0]?.key ?? '');
  const activeTab = controlledActiveTab ?? internalTab;

  const handleTabChange = useCallback(
    (key: string) => {
      if (onTabChange) {
        onTabChange(key);
      } else {
        setInternalTab(key);
      }
    },
    [onTabChange],
  );

  const hasHeader = !!(title || subtitle || actions);

  // 将 PageTab[] 转换为 antd Tabs items 格式
  const tabItems = tabs?.map((tab) => ({
    key: tab.key,
    label: tab.tab,
    disabled: tab.disabled,
  }));

  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', gap, ...style }}
    >
      {/* 页头 */}
      {hasHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: token.marginSM,
          }}
        >
          <div style={{ flex: 1 }}>
            {title && (
              <h2
                style={{
                  margin: 0,
                  fontSize: token.fontSizeHeading4,
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: token.fontSize,
                  color: token.colorTextTertiary,
                  marginTop: token.marginXXS,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {headerExtra}
          {actions && (
            <div
              style={{ display: 'flex', gap: token.marginXS, flexShrink: 0 }}
            >
              {actions}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      {tabItems && tabItems.length > 0 && (
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
        />
      )}

      {/* 内容 */}
      {wrapCard ? <Card>{children}</Card> : children}
    </div>
  );
}
